import { motion } from "framer-motion";
import { 
  ArrowRight, Users, Award, Globe, Star,
  Cpu, Shield, Building2, Monitor, Calendar, Clock,
  Briefcase, GraduationCap, Server, UserCheck, TrendingUp
} from "lucide-react";
import { SEO } from "@/components/shared";
import { MEDIA, fadeInUp, staggerContainer, viewportOnce } from "@/constants";

const ZOOM_URL = "https://us06web.zoom.us/meeting/register/zKItFvECQXG8ufdaaf489Q#/registration";

const AUDIENCE = [
  { id: 'aud-deans', icon: Building2, title: "Deans & Principals", desc: "Understand strategic advantages of Intel Unnati for your institution's ranking and placements." },
  { id: 'aud-hods', icon: Briefcase, title: "HODs & Department Heads", desc: "Explore AI curriculum integration, lab setup, and AICTE-aligned certification pathways." },
  { id: 'aud-prof', icon: GraduationCap, title: "Professors & Academicians", desc: "Discover exclusive FDPs & Certifications on AI, Confidential Computing, DL, HPC & Related Technologies with hands-on training opportunities." },
];

const TAKEAWAYS = [
  { id: 'learn-labs', icon: Server, text: "Intel Unnati AI Labs \u2014 state-of-the-art infrastructure for hands-on AI education, already in 275+ colleges." },
  { id: 'learn-cert', icon: Award, text: "Industry-relevant certifications aligned with AICTE recommendations, guided by Intel & industry mentors." },
  { id: 'learn-faculty', icon: UserCheck, text: "Exclusive Faculty Development Programs on AI for select institutions attending this webinar." },
  { id: 'learn-student', icon: Users, text: "Student upskilling pathways \u2014 from Data Science to Generative AI, with access to Intel-powered AI PCs." },
  { id: 'learn-partner', icon: Globe, text: "How to become an Intel Unnati partner institution through Qbic Catalyst's facilitation." },
  { id: 'learn-placement', icon: TrendingUp, text: "Enhance employability metrics and placements with industry-recognized AI credentials." },
];

const CREDENTIALS = [
  { id: 'cred-intel', icon: Cpu, title: "Intel Unnati Partner", desc: "Official system integrator for Intel Unnati program across institutions." },
  { id: 'cred-iso', icon: Shield, title: "ISO 27001:2022", desc: "Internationally certified for information security management." },
  { id: 'cred-govt', icon: Building2, title: "Govt. of India Recognized", desc: "Recognized Ed-Tech firm under Government of India initiatives." },
  { id: 'cred-tech', icon: Globe, title: "Tech Ecosystem Alliances", desc: "Close collaborations with Intel, Dell, Lenovo & Conquer Technologies." },
];

const handleRegister = () => {
  window.open(ZOOM_URL, "_blank");
};

export default function WebinarPage() {
  return (
    <div className="pt-0">
      <SEO
        title="Webinar: Intel Unnati Program Briefing"
        description="Join Qbic Catalyst Partners for an exclusive webinar on Intel Unnati \u2014 a transformative AI education program for higher education institutions."
        keywords="Intel Unnati webinar, AI education, higher education, faculty development, AI labs, AICTE, Qbic Catalyst, Intel partnership"
        path="/webinar/intel-unnati"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A1628 0%, #0F2240 40%, #0A1628 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-10" style={{ background: 'radial-gradient(ellipse, #C9A962, transparent 70%)' }} />

        <div className="container-custom relative z-10 py-28 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial="initial" animate="animate" variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 bg-[#C9A962]/10 border border-[#C9A962]/25 rounded-full px-3 py-1 text-[#C9A962] text-xs font-semibold uppercase tracking-wider">Qbic Catalyst Partners</span>
                <span className="text-[#2A3648] text-xl font-light">x</span>
                <span className="inline-flex items-center gap-1.5 bg-[#0071C5]/10 border border-[#0071C5]/25 rounded-full px-3 py-1 text-[#0071C5] text-xs font-semibold">Intel Unnati</span>
              </motion.div>

              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#C9A962]/10 border border-[#C9A962]/25 rounded-full px-4 py-1.5 mb-6">
                <Monitor size={14} className="text-[#C9A962]" />
                <span className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider">Live Webinar</span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-[1.1]">
                Intel<span className="text-[#0071C5]">&reg;</span> Unnati{' '}
                <span className="text-gradient">Program Briefing</span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-[#A0ABC0] mb-8 max-w-xl leading-relaxed">
                Discover how your institution can access world-class AI infrastructure, faculty development, and industry-aligned certifications.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 mb-10">
                <div className="flex items-center gap-2 text-white">
                  <Calendar size={18} className="text-[#C9A962]" />
                  <span className="font-semibold">09 May 2026</span>
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Clock size={18} className="text-[#C9A962]" />
                  <span className="font-semibold">11:00 AM IST</span>
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Monitor size={18} className="text-[#C9A962]" />
                  <span className="font-semibold">Online</span>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <button onClick={handleRegister} data-testid="webinar-register-hero" className="btn-primary text-base px-10 py-4 flex items-center gap-3 shadow-lg shadow-[#C9A962]/20 hover:shadow-[#C9A962]/40 transition-shadow">
                  Register Now &#8212; It's Free <ArrowRight size={20} />
                </button>
                <p className="text-[#A0ABC0] text-xs mt-3">Limited seats. Priority for institutional decision-makers.</p>
              </motion.div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="flex justify-center lg:justify-end">
              <div className="bg-[#121E30] border border-[#2A3648] rounded-2xl p-8 max-w-sm w-full shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #C9A962, transparent 70%)' }} />
                <p className="text-[#C9A962] text-xs uppercase tracking-[0.2em] mb-4 font-semibold">Your Presenter</p>
                <img src={MEDIA.expertLeader} alt="Mr. Praveen Kumar" className="w-full aspect-[4/5] object-cover object-top rounded-xl mb-6 border border-[#2A3648]" />
                <h3 className="text-xl font-bold text-white mb-1">Mr. Praveen Kumar</h3>
                <p className="text-[#C9A962] text-sm font-medium mb-3">Director & Founder, Qbic Catalyst Partners</p>
                <p className="text-[#A0ABC0] text-sm leading-relaxed">
                  Ex-Field Marketing Head, Dell Technologies. Intel Certified AI Coach, Board Advisor. 20+ Years experience in IT Solutions, Advisory & Training.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Attend */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="mb-12">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Who Should Attend</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Calling All Higher Education Leaders</h2>
              <p className="text-[#A0ABC0] text-lg max-w-2xl">This webinar is designed for academic leaders who want to future-proof their institutions with AI-powered education infrastructure.</p>
            </motion.div>
            <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {AUDIENCE.map((item) => (
                <motion.div key={item.id} variants={fadeInUp} className="p-6 bg-[#121E30] rounded-xl border border-[#2A3648] card-hover">
                  <div className="icon-container mb-5 w-14 h-14"><item.icon className="text-[#C9A962]" size={24} /></div>
                  <h4 className="text-lg font-bold mb-2">{item.title}</h4>
                  <p className="text-[#A0ABC0] text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="section bg-[#1A2A40]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Webinar Highlights</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What You'll Take Away</h2>
              <p className="text-[#A0ABC0] text-lg max-w-2xl mx-auto">A concise, high-impact session covering the Intel Unnati program and how your institution can benefit.</p>
            </motion.div>
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {TAKEAWAYS.map((item) => (
                <motion.div key={item.id} variants={fadeInUp} className="flex items-start gap-4 p-5 bg-[#0A1628] rounded-xl border border-[#2A3648]">
                  <div className="w-10 h-10 bg-[#C9A962]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"><item.icon className="text-[#C9A962]" size={20} /></div>
                  <p className="text-[#A0ABC0] text-sm leading-relaxed">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Exclusive Incentive Banner */}
      <section className="py-16 md:py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A2A40 0%, #0A1628 100%)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(#C9A962 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={viewportOnce} className="bg-gradient-to-r from-[#C9A962]/10 to-[#C9A962]/5 border border-[#C9A962]/30 rounded-2xl p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 bg-[#C9A962]/15 rounded-full px-4 py-1.5 mb-6">
              <Star size={14} className="text-[#C9A962]" />
              <span className="text-[#C9A962] text-xs font-semibold uppercase tracking-wider">Exclusive for Attendees</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">Free Faculty Development Programs on AI</h2>
            <p className="text-[#A0ABC0] text-lg max-w-2xl mx-auto mb-8">Select institutions attending this webinar will receive complimentary, hands-on AI training programs for their faculty &#8212; delivered by Qbic Catalyst's expert trainers in collaboration with Intel.</p>
            <button onClick={handleRegister} data-testid="webinar-register-incentive" className="btn-primary text-base px-8 py-3.5 inline-flex items-center gap-2">
              Secure Your Spot <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Why QCP */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer}>
            <motion.div variants={fadeInUp} className="text-center mb-14">
              <p className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">Your Trusted Partner</p>
              <h2 className="text-3xl md:text-4xl font-bold">Why Qbic Catalyst Partners</h2>
            </motion.div>
            <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
              {CREDENTIALS.map((item) => (
                <motion.div key={item.id} variants={fadeInUp} className="text-center p-6 bg-[#121E30] rounded-xl border border-[#2A3648]">
                  <div className="icon-container mx-auto mb-4 w-14 h-14"><item.icon className="text-[#C9A962]" size={24} /></div>
                  <h4 className="font-bold mb-2 text-sm">{item.title}</h4>
                  <p className="text-[#A0ABC0] text-xs">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section bg-[#1A2A40] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#C9A962 1px, transparent 1px), linear-gradient(90deg, #C9A962 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="container-custom text-center relative z-10">
          <motion.div initial="initial" whileInView="animate" viewport={viewportOnce} variants={staggerContainer} className="max-w-2xl mx-auto">
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Don't Miss This <span className="text-gradient">Opportunity</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] text-lg mb-4">
              Join us on <strong className="text-white">9th May 2026 at 11:00 AM IST</strong> to explore how Intel Unnati can transform your institution's AI capabilities.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] text-sm mb-10">Limited seats available. Register now to reserve yours.</motion.p>
            <motion.div variants={fadeInUp}>
              <button onClick={handleRegister} data-testid="webinar-register-final" className="btn-primary text-lg px-12 py-4 inline-flex items-center gap-3 shadow-lg shadow-[#C9A962]/20 hover:shadow-[#C9A962]/40 transition-shadow">
                Register for the Webinar <ArrowRight size={20} />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
